import { Bot, MessageSquare, Send, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

const ACCENT = "#8C5EBA";
const ACCENT_LIGHT = "#f3eefa";

const initialMessage =
  "Hi! I'm the OPHO Digital assistant. Ask me about our services — Brand Strategy, Content Creation, SEO, PR & Social Media, Performance Marketing, or E-Commerce Growth — or anything else on the website.";

const quickPrompts = [
  "What services does OPHO Digital offer?",
  "How do you approach SEO and digital presence?",
  "How can I get in touch?",
];

export default function ChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [input, setInput] = useState("");
  const [error, setError] = useState(null);
  const [messages, setMessages] = useState([
    { role: "assistant", content: initialMessage },
  ]);
  const viewportRef = useRef(null);

  useEffect(() => {
    if (viewportRef.current) {
      viewportRef.current.scrollTop = viewportRef.current.scrollHeight;
    }
  }, [messages, isLoading]);

  // Textarea auto-grow
  const textareaRef = useRef(null);
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, 120) + "px";
  }, [input]);

  async function sendMessage(message) {
    const trimmed = message.trim();
    if (!trimmed || isLoading) return;

    const nextMessages = [...messages, { role: "user", content: trimmed }];
    const history = nextMessages
      .slice(0, -1)
      .map(({ role, content }) => ({ role, content }));

    setMessages(nextMessages);
    setInput("");
    setError(null);
    setIsLoading(true);

    try {
      const response = await fetch("/api/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: trimmed,
          path: window.location.pathname,
          history,
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.reply) {
        throw new Error(data.error || "The assistant is temporarily unavailable. Please try again.");
      }

      setMessages((curr) => [...curr, { role: "assistant", content: data.reply }]);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }

  function handleKey(e) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  }

  return (
    <div className="fixed bottom-5 right-5 z-[60]">
      {isOpen ? (
        <div
          className="overflow-hidden shadow-2xl"
          style={{
            width: "22rem",
            maxWidth: "calc(100vw - 2.5rem)",
            borderRadius: "1.5rem",
            border: "1px solid rgba(140,94,186,0.15)",
            background: "#fff",
          }}
        >
          {/* Header */}
          <div
            className="flex items-start justify-between gap-4 px-5 py-4 text-white"
            style={{ background: "linear-gradient(135deg,#1a0a2e 0%,#4a1d8a 100%)" }}
          >
            <div>
              <p style={{ fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.2em", textTransform: "uppercase", color: "rgba(214,200,223,0.9)" }}>
                OPHO Digital Assistant
              </p>
              <h2 className="mt-1.5 text-base font-bold leading-snug">
                Ask about services, strategy &amp; growth
              </h2>
            </div>
            <button
              type="button"
              aria-label="Close chat"
              onClick={() => setIsOpen(false)}
              className="rounded-full border p-2 transition"
              style={{ border: "1px solid rgba(255,255,255,0.2)", color: "rgba(255,255,255,0.8)" }}
            >
              <X size={16} />
            </button>
          </div>

          {/* Messages */}
          <div
            ref={viewportRef}
            className="space-y-3 overflow-y-auto px-4 py-4"
            style={{ maxHeight: "22rem", background: ACCENT_LIGHT }}
          >
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className="max-w-[88%] rounded-[1.25rem] px-4 py-3 text-sm leading-relaxed shadow-sm"
                style={
                  msg.role === "assistant"
                    ? { marginRight: "auto", background: "#fff", color: "#374151" }
                    : { marginLeft: "auto", background: ACCENT, color: "#fff" }
                }
              >
                {msg.content}
              </div>
            ))}

            {isLoading && (
              <div
                className="inline-flex rounded-[1.25rem] px-4 py-3 text-sm shadow-sm"
                style={{ background: "#fff", color: "#9ca3af" }}
              >
                Thinking...
              </div>
            )}

            {error && (
              <div className="rounded-[1.25rem] px-4 py-3 text-sm" style={{ background: "#fef2f2", border: "1px solid #fecaca", color: "#b91c1c" }}>
                {error}
              </div>
            )}
          </div>

          {/* Input area */}
          <div className="border-t px-4 py-4" style={{ borderColor: "rgba(140,94,186,0.1)", background: "#fff" }}>
            {/* Quick prompts */}
            <div className="mb-3 flex flex-wrap gap-1.5">
              {quickPrompts.map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => sendMessage(p)}
                  className="rounded-full px-3 py-1.5 text-xs font-medium transition"
                  style={{ border: "1px solid rgba(140,94,186,0.2)", color: "#6b7280" }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = ACCENT; e.currentTarget.style.color = ACCENT; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = "rgba(140,94,186,0.2)"; e.currentTarget.style.color = "#6b7280"; }}
                >
                  {p}
                </button>
              ))}
            </div>

            <form
              onSubmit={(e) => { e.preventDefault(); sendMessage(input); }}
              className="flex items-end gap-2"
            >
              <label className="sr-only" htmlFor="opho-chat-input">Ask OPHO Digital</label>
              <textarea
                ref={textareaRef}
                id="opho-chat-input"
                rows={1}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKey}
                placeholder="Ask about OPHO Digital..."
                className="flex-1 resize-none rounded-2xl px-4 py-3 text-sm outline-none transition"
                style={{
                  minHeight: "3rem",
                  maxHeight: "7.5rem",
                  border: "1px solid rgba(140,94,186,0.2)",
                  background: ACCENT_LIGHT,
                  color: "#374151",
                }}
                onFocus={(e) => { e.target.style.borderColor = ACCENT; }}
                onBlur={(e) => { e.target.style.borderColor = "rgba(140,94,186,0.2)"; }}
              />
              <button
                type="submit"
                disabled={isLoading || !input.trim()}
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-white transition hover:scale-105 disabled:cursor-not-allowed disabled:opacity-50"
                style={{ background: ACCENT }}
              >
                <Send size={16} />
              </button>
            </form>

            <Link
              to="/contact"
              className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold transition"
              style={{ color: ACCENT }}
              onClick={() => setIsOpen(false)}
            >
              Book a Strategic Call <MessageSquare size={13} />
            </Link>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setIsOpen(true)}
          className="group inline-flex items-center gap-3 rounded-full px-5 py-3.5 text-left text-white shadow-2xl transition hover:-translate-y-0.5"
          style={{ background: "#1a0a2e", boxShadow: "0 8px 32px rgba(140,94,186,0.35)" }}
        >
          <span
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-white"
            style={{ background: ACCENT }}
          >
            <Bot size={18} />
          </span>
          <span className="hidden sm:block">
            <span className="block text-[0.6rem] font-bold uppercase tracking-[0.2em]" style={{ color: "rgba(214,200,223,0.85)" }}>
              Website Assistant
            </span>
            <span className="mt-0.5 block text-sm font-semibold">Ask OPHO Digital</span>
          </span>
        </button>
      )}
    </div>
  );
}
